import React, { useEffect, useMemo, useState } from 'react';
import { buildInventoryRecallSummary, findInventoryRecallMatches, recallMatchLabel } from '../inventory/inventoryRecallModel';
import './admin.css';

export default function InventoryRecallLookup({ profile }) {
  const [data, setData] = useState({ expirations: [] });
  const [status, setStatus] = useState('loading');
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [includeArchived, setIncludeArchived] = useState(false);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      setStatus('loading');
      setError('');
      try {
        const response = await fetch('/api/tracking?admin=inventory', { cache: 'no-store' });
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(payload?.error || 'Unable to load inventory ledger.');
        if (!alive) return;
        setData(payload);
        setStatus('ready');
      } catch (err) {
        if (!alive) return;
        setError(err?.message || 'Unable to load inventory ledger.');
        setStatus('error');
      }
    };
    load();
    return () => { alive = false; };
  }, []);

  const matches = useMemo(
    () => findInventoryRecallMatches(data.expirations, query, { includeArchived }),
    [data.expirations, query, includeArchived],
  );
  const summary = useMemo(() => buildInventoryRecallSummary(matches), [matches]);
  const searching = query.trim().length > 0;

  if (!profile?.is_admin) {
    return <div className="admin-root"><main className="admin-page"><section className="admin-card admin-denied"><h1>Admin access required</h1><button type="button" onClick={() => window.location.assign('/')}>Back</button></section></main></div>;
  }

  return (
    <div className="admin-root">
      <header className="admin-topbar">
        <div>
          <h1>Lot / Recall Lookup</h1>
          <p>Find every active physical unit by lot, product, catalog ID, GTIN/barcode, expiration, or container.</p>
        </div>
        <button type="button" onClick={() => window.location.assign('/admin')}>Admin Home</button>
      </header>
      <main className="admin-page">
        <section className="admin-card">
          <div className="admin-section-head">
            <div>
              <h2>Search inventory ledger</h2>
              <p>Paste a lot number or barcode value from an EMS recall notice to see exactly where matching stock is located.</p>
            </div>
          </div>
          <label>Lot, GTIN, product, catalog ID, expiration, or container
            <input
              value={query}
              onChange={event => setQuery(event.target.value)}
              placeholder="A4640025, 00325021318027, Adenosine, ALS-040…"
              autoFocus
            />
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12 }}>
            <input type="checkbox" checked={includeArchived} onChange={event => setIncludeArchived(event.target.checked)} />
            Include removed / archived inventory history
          </label>
        </section>

        {status === 'loading' && <section className="admin-card">Loading inventory ledger…</section>}
        {error && <section className="admin-card admin-denied"><strong>{error}</strong></section>}

        {status === 'ready' && searching && (
          <>
            <div className="admin-summary-grid">
              <div className="admin-stat"><span>Active Units</span><strong>{summary.activeUnits}</strong></div>
              <div className="admin-stat"><span>Containers</span><strong>{summary.containers}</strong></div>
              <div className="admin-stat"><span>Products</span><strong>{summary.products}</strong></div>
              {includeArchived && <div className="admin-stat"><span>Archived Units</span><strong>{summary.archivedUnits}</strong></div>}
            </div>

            <section className="admin-card">
              <div className="admin-section-head">
                <div><h2>Matches</h2><p>{matches.length ? 'Active stock is listed first. Archived history is clearly marked.' : 'No matching inventory was found.'}</p></div>
                <div className="admin-day-pill">{matches.length} records</div>
              </div>
              {!matches.length && <div className="admin-empty">No match for “{query.trim()}”.</div>}
              {matches.map(record => (
                <div className="admin-table-row" key={record.recordId}>
                  <span>
                    <strong>{record.item || record.catalogId}</strong>
                    <small>{record.setName || record.setId} · {record.component || 'No component'} · {record.catalogId}</small>
                    <small>{recallMatchLabel(record)}</small>
                  </span>
                  <strong>{record.returnedToLogistics ? 'ARCHIVED' : 'ACTIVE'}</strong>
                </div>
              ))}
            </section>
          </>
        )}

        {status === 'ready' && !searching && (
          <section className="admin-card admin-empty">Enter a lot number, product, GTIN/barcode, expiration date, or container to search.</section>
        )}
      </main>
    </div>
  );
}
